import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EncounterStateModel } from "./encounter-model";
import { MessageService } from "primeng/api";
import { EncounterFilter, EncounterService } from "../../services/encounter.service";
import { Encounters } from "./encounter-actions";
import { forkJoin, lastValueFrom, map, switchMap, tap } from "rxjs";
import { append, iif, patch, removeItem, updateItem } from "@ngxs/store/operators";
import { TealCompEncounter, TealCompEncounterDetails, TealCompEncounterHeader } from "../../models/encounter";
import { BlockUI, Fax, Note, NoteAddendum, UserService } from "@harlowe/core";
import { UserInterface } from "../ui/ui-actions";
import { Referrals } from "../referrals/referral-actions";
import { instanceToInstance, plainToClass, plainToInstance } from "class-transformer";
import { Diary } from "@harlowe/core";
import { Users } from "@harlowe/core"
import { ReceivedEmail, SentEmail } from "@harlowe/core";
import { Emails } from "@harlowe/core";

@State<EncounterStateModel>
(
    {
        name: "encounters",
        defaults: 
        {
            encounterSearchResults: new Map<string,TealCompEncounterHeader[]>(),
            openEncounters: [],
            persistedEncounterIds: [],
            activeEncounterFilter: undefined,
            activeEncounterIndex: 0,
            activeEncounter: undefined,
            activeEncounterId: undefined
        }
    }
)

  @Injectable()
  export class EncounterState 
  {

    constructor
    (
        private _encounterService: EncounterService,
        private _userService: UserService,
        private _messageService: MessageService
    )
    {

    }

    @Selector()
    static activeEncounterFilter(state: EncounterStateModel) 
    {
      return instanceToInstance(state.activeEncounterFilter);
    }

    @Selector()
    static openEncounters(state:EncounterStateModel)
    {
        return state.openEncounters;
    }

    @Selector()
    static encounterSearchResults(state:EncounterStateModel)
    {
        return state.encounterSearchResults;
    }

    @Selector()
    static activeEncounter(state:EncounterStateModel)
    {
        return state.activeEncounter;
    }

    @Selector()
    static activeEncounterIndex(state:EncounterStateModel)
    {
        return state.activeEncounterIndex;
    }

    @Selector()
    static activeEncounterDiaries(state:EncounterStateModel)
    {
        return state.activeEncounter?.diaries;
    }

    
    
    @Action([Users.UserLoggedIn])
    async userLoggedIn(ctx: StateContext<EncounterStateModel>, action: Users.UserLoggedIn)
    {

        var state = ctx.getState();

        //Retrieve the state from persistence
        // var persistedState = await this._userService.getSetting<EncounterStateModel>("encounterState");
        var persistedEncounters = await this._userService.getSetting<PersistedEncounters>("persistedEncounters");

        var filter = new EncounterFilter();

        if(!persistedEncounters)
        {
            // Initialize state
            await this._userService.setSetting("persistedEncounters", { persistedEncounterIds: [], activeEncounterIndex: 0, activeEncounterFilter: undefined } as PersistedEncounters);
        }
        else
        {
            var openEncounters: TealCompEncounter[] = [ ];

            for (const id of persistedEncounters.persistedEncounterIds)
            {
               var encounter = await lastValueFrom(this._encounterService.getEncounter(id)); 
               openEncounters.push(encounter)
            }

            if (persistedEncounters.activeEncounterFilter != undefined)
                filter = persistedEncounters.activeEncounterFilter;

            ctx.setState
            (
                {
                    ...state,
                    openEncounters: openEncounters,
                    persistedEncounterIds: persistedEncounters.persistedEncounterIds
                }
            )        
    
            if (persistedEncounters.persistedEncounterIds.length > 0)
                await lastValueFrom(ctx.dispatch(new Encounters.EncounterSelected({index:persistedEncounters.activeEncounterIndex})));

                


        }

        filter.includeUnassigned = true;

        await lastValueFrom(ctx.dispatch(new Encounters.EncounterFilterSet({filter:filter})));        
    }

    @Action([Encounters.EncounterFilterSet])
    async filterSet(ctx: StateContext<EncounterStateModel>, action:Encounters.EncounterFilterSet)
    {
        const state = ctx.getState();

        //Search based on the current filter
        var filter = action.payload.filter;
        var results = await this._encounterService.searchForEncounters(filter);


        var encounterSearchResults = new Map<string,TealCompEncounterHeader[]>();

        for (const [encounterId, result] of results.entries())
        {
            if (result.searchTerms.length == 0)
            {
                if (!encounterSearchResults.has("All Results"))
                {
                    encounterSearchResults.set("All Results", [] as TealCompEncounterHeader[]);
                }
                encounterSearchResults.get("All Results")!.push(result.encounter as TealCompEncounterHeader);
            }

            for (const category of result.searchTerms)
            {
                if (!encounterSearchResults.has(category))
                {
                    encounterSearchResults.set(category, [] as TealCompEncounterHeader[]);
                }
                encounterSearchResults.get(category)!.push(result.encounter as TealCompEncounterHeader);
            }

        }

        ctx.setState
        (
            {
                ...state,
                encounterSearchResults: encounterSearchResults,
                activeEncounterFilter: filter
            }
        )



        await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
    }


    @Action([Emails.EmailServiceEmailUpdated ],{cancelUncompleted: true})
    async encounterEmailUpdated(ctx:StateContext<EncounterStateModel>,action:Emails.EmailServiceEmailUpdated)
    {
        //See if active encounter owns this email
        var existingState =ctx.getState();
        if(existingState.activeEncounter && existingState.activeEncounter.sentEmails.map(e => e.id).includes(action.payload.email.id))
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter:patch
                    (
                        {
                            sentEmails: updateItem(e => e.id == action.payload.email.id, action.payload.email)
                        }
                    )
                })
            )

            //And, persist
            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }
        await lastValueFrom(ctx.dispatch(new Referrals.EncounterStateEncounterEmailUpdated({email:action.payload.email})));

    }

    @Action([Encounters.EncounterServiceEncounterEmailSent ],{cancelUncompleted: true})
    async encounterEmailSent(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceEncounterEmailSent)
    {
        var existingState = ctx.getState();
        if(!existingState.activeEncounter?.sentEmails.map(e => e.id).includes(action.payload.email.id))
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            sentEmails: append([action.payload.email])
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }
        await lastValueFrom(ctx.dispatch(new Referrals.EncounterStateEncounterEmailSent({encounterId:action.payload.encounterId,email:action.payload.email})));


    }


    @Action([Encounters.EncounterServiceEncounterEmailReceived ],{cancelUncompleted: true})
    async encounterEmailReceived(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceEncounterEmailReceived)
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId && !existingState.activeEncounter?.receivedEmails.map(e => e.id).includes(action.payload.email.id))
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            receivedEmails: append([action.payload.email])
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }
        await lastValueFrom(ctx.dispatch(new Referrals.EncounterStateEncounterEmailReceived({encounterId:action.payload.encounterId,email:action.payload.email})));

    }


    @Action([Encounters.EncounterServiceAttachmentDeleted])
    async encounterAttachmentDeleted(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceAttachmentDeleted)
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.attachments.map(a => a.id).includes(action.payload.file.id))
        {
            var index = existingState.activeEncounter.attachments.findIndex(a => a.id == action.payload.file.id)

            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            attachments: removeItem(index)
                        }
                    )
                })
            )
            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));

        }        
        await lastValueFrom(ctx.dispatch(new Referrals.EncounterStateAttachmentDeleted({encounterId:action.payload.encounterId,file:action.payload.file})));

    }

    @Action([Encounters.EncounterServiceAttachmentAdded])
    async encounterAttachmentAdded(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceAttachmentAdded)
    {
        var existingState = ctx.getState();
        if(!existingState.activeEncounter?.attachments.map(a => a.id).includes(action.payload.file.id))
        {

            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            attachments: append([action.payload.file])
                        }
                    )
                })
            )
            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));

        }
        await lastValueFrom(ctx.dispatch(new Referrals.EncounterStateAttachmentAdded({encounterId:action.payload.encounterId,file:action.payload.file})));

    }

    @Action([Encounters.EncounterServiceAttachmentsAdded])
    async encounterAttachmentsAdded(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceAttachmentsAdded)
    {
        var existingState = ctx.getState();
        var existingIds = existingState.activeEncounter?.attachments.map(a => a.id);
        var attachmentsToAdd = action.payload.files.filter(a => !existingIds?.includes(a.id) )

        if(attachmentsToAdd && attachmentsToAdd.length > 0)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            attachments: append(action.payload.files)
                        }
                    )
                })
            )
            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));

        }
        await lastValueFrom(ctx.dispatch(new Referrals.EncounterStateAttachmentsAdded({encounterId:action.payload.encounterId,files:action.payload.files})));

    }


    @Action([Encounters.EncounterUpdated], { cancelUncompleted: true })
    async encounterUpdated(ctx: StateContext<EncounterStateModel>, action:Encounters.EncounterUpdated)
    {

        var header = action.payload.header;
        var newEncounter = action.payload.encounter;
        const state = ctx.getState();

        var updatedResults = new Map<string,TealCompEncounterHeader[]>();

        for (const [category, value] of state.encounterSearchResults.entries())
        {
            updatedResults.set(category, value.map(e => e.id == header.id ? header : e));
        }




        ctx.setState
        (
            patch<EncounterStateModel>
            ({
                //Update the header in the list of headers (if it's there)
                encounterSearchResults: updatedResults,

                //Same thing for the list of open encounters, if it's there
                openEncounters: updateItem
                (
                    item => item.id == newEncounter.id,
                    newEncounter
                )


            })
        )
    
        var newState = ctx.getState();
        if(state.activeEncounter!=null && state.activeEncounter.id == header.id)
        {
            // var encounter = structuredClone(newEncounter) as TealCompEncounterDetails;

            // ctx.setState
            // (
            //     {
            //         ...newState,
            //         activeEncounter:encounter
            //     }
            // )
            await lastValueFrom(ctx.dispatch(new Encounters.EncounterSelected({index:state.activeEncounterIndex})))
        }
        else
        {
            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }
                
    }


    @Action([Encounters.EncounterOpenedFromEncounterManager, Encounters.EncounterOpenedFromClaimCard, Encounters.EncounterOpenedFromReferralManager, Encounters.EncounterOpenedFromDiary, Encounters.EncounterOpenedFromEmailSidebar])
    encounterOpened(ctx: StateContext<EncounterStateModel>, action: Encounters.EncounterOpenedFromEncounterManager | Encounters.EncounterOpenedFromClaimCard | Encounters.EncounterOpenedFromReferralManager | Encounters.EncounterOpenedFromDiary | Encounters.EncounterOpenedFromEmailSidebar)
    {
        var state = ctx.getState();
        var encounterId : number;
        if(action instanceof Encounters.EncounterOpenedFromReferralManager || action instanceof Encounters.EncounterOpenedFromDiary || action instanceof Encounters.EncounterOpenedFromEmailSidebar)
        {
            encounterId = action.payload.encounterId;
            ctx.dispatch(new UserInterface.CloseSidebar());
        }
        else
        {
            encounterId = action.payload.header.id;
        }

        //get the full encounter, and add it to the list of open encounters
        return ctx.dispatch(new BlockUI.BlockUI())
        .pipe
        (        
            switchMap
            (
                val => this._encounterService.getEncounter(encounterId)
            ),
            map
            (
                encounter =>
                {
                    var maxIndex = state.openEncounters.length;

                    var finalIndex = state.openEncounters.map(e => e.id).includes(encounter.id) ? state.openEncounters.findIndex(e => e.id == encounter.id) : maxIndex;

                    ctx.setState
                    (
                        iif(!state.openEncounters.map(e => e.id).includes(encounter.id),
                        {
                            ...state,
                            openEncounters:[...state.openEncounters,encounter],
                            persistedEncounterIds:[...state.persistedEncounterIds, encounter.id],
                            activeEncounterIndex:maxIndex,
                        },
                        {
                            ...state,
                            activeEncounterIndex: state.openEncounters.findIndex(e => e.id == encounter.id)
                        })
                        // patch<EncounterStateModel>
                        // ({
                        //     openEncounters: insertItem
                        //     (
                        //         encounter
                        //     ),
                        //     activeEncounterIndex: maxIndex
                        // }))
                    )

                    return finalIndex;
                }
            ),
            tap
            (
                i => ctx.dispatch(new Encounters.EncounterSelected({index:i}))
            ),
            tap
            (
                () => ctx.dispatch(new BlockUI.UnBlockUI())
            )

        )


        

    }

    @Action([Encounters.WorkbenchEncounterClosed])
    async encounterClosed(ctx: StateContext<EncounterStateModel>, action: Encounters.WorkbenchEncounterClosed)
    {
        var state = ctx.getState();

        //get the full encounter, and add it to the list of open encounters
        var encounter =  action.payload.encounter;
        var indexAfterClose = state.openEncounters.length - 2;

        ctx.setState
        (
            patch<EncounterStateModel>
            ({
                openEncounters: removeItem
                (
                    item=> item.id === encounter.id
                ),
                persistedEncounterIds: removeItem
                (
                    id => id === encounter.id
                )
            })
        )
        
        if(indexAfterClose >= 0)
            await lastValueFrom(ctx.dispatch(new Encounters.EncounterSelected({index:indexAfterClose})))
        await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
    }


    @Action([Encounters.EncounterServiceEncounterClosed])
    async encounterManagerEncounterClosed(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceEncounterClosed)
    {
        //Update the entry for this encounter in the list of found encounters
        //Hide it from the results if it doesn't match filters
        var state = ctx.getState();

        var updatedResults = new Map<string,TealCompEncounterHeader[]>();
    
        if(state.activeEncounterFilter?.statuses && state.activeEncounterFilter.statuses.includes(6))
        {
            for (const [category, value] of state.encounterSearchResults.entries())
            {
                updatedResults.set(category, value.map(e => e.id == action.payload.encounter.id ? action.payload.encounter : e));
            }

            ctx.setState
            (
                patch
                ({
                    encounterSearchResults: updatedResults
                })
            )
        }
        else
        {  
            for (const [category, value] of state.encounterSearchResults.entries())            
            {
                updatedResults.set(category, value.filter(e => e.id != action.payload.encounter.id));
            }

            ctx.setState
            (
                patch
                ({
                    encounterSearchResults: updatedResults
                })
            )
        }
    }

    @Action([Encounters.EncounterSelected],{cancelUncompleted: true})
    encounterSelected(ctx: StateContext<EncounterStateModel>,action: Encounters.EncounterSelected)
    {

        //Get the details for the encounter
        var state = ctx.getState();

        var encounter = structuredClone(state.openEncounters[action.payload.index]) as TealCompEncounterDetails;
        encounter = plainToInstance(TealCompEncounterDetails, encounter);
        
        return forkJoin
        (
            [this._encounterService.getEncounterNotes(encounter.id),
            this._encounterService.getEncounterDiaries(encounter.id),
            this._encounterService.getEncounterSentEmails(encounter.id),
            this._encounterService.getEncounterReceivedEmails(encounter.id),
            this._encounterService.getEncounterFaxes(encounter.id),
            ]
        )
        .pipe
        (
            tap
            (
                val => 
                {
                    encounter.notes = val[0].map(n => plainToClass(Note, n));
                    encounter.diaries = val[1].map(v => plainToClass(Diary, v));
                    encounter.sentEmails = val[2].map(e => plainToClass(SentEmail, e));
                    encounter.receivedEmails = val[3].map(e => plainToClass(ReceivedEmail, e));
                    encounter.outboundFaxes = val[4].map(f => plainToClass(Fax, f));
                    console.log(encounter);
                    ctx.setState
                    (
                        {
                            ...state,
                            activeEncounter: encounter,
                            activeEncounterIndex:action.payload.index
                        }
                    )
                    ctx.dispatch(new Encounters.PersistState())
                }
            )
        )
    } 

    @Action([Encounters.PersistState],{cancelUncompleted: true})
    async persistState(ctx:StateContext<EncounterStateModel>)
    {
        var state = ctx.getState();

        await this._userService.setSetting("persistedEncounters", { persistedEncounterIds: state.persistedEncounterIds, activeEncounterIndex: state.activeEncounterIndex, activeEncounterFilter: state.activeEncounterFilter } as PersistedEncounters);
        await lastValueFrom(ctx.dispatch(new BlockUI.UnBlockUI()));
    }


    @Action ([Encounters.NoteAdded, Encounters.NoteUpdated])
    async encounterNoteAdded
    (
        ctx:StateContext<EncounterStateModel>,
        action:Encounters.NoteAdded | Encounters.NoteUpdated
    )
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            notes: iif( action instanceof Encounters.NoteAdded ,append([plainToInstance(Note,action.payload.note)]), updateItem(n => n.id == action.payload.note.id, plainToInstance(Note,action.payload.note)))
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));            
        }
        if(action instanceof Encounters.DiaryAdded)
            await lastValueFrom(ctx.dispatch(new Referrals.NoteAdded({encounterId:action.payload.encounterId,note:action.payload.note})));
        else
            await lastValueFrom(ctx.dispatch(new Referrals.NoteUpdated({encounterId:action.payload.encounterId,note:action.payload.note})));

    }

    @Action (Encounters.NoteDeleted)
    async encounterNoteDeleted
    (
        ctx:StateContext<EncounterStateModel>,
        action:Encounters.NoteDeleted
    )
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            notes: removeItem<Note>(note=>note.id === action.payload.noteId)
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
            await lastValueFrom(ctx.dispatch(new Referrals.NoteDeleted({encounterId:action.payload.encounterId, noteId:action.payload.noteId})));
        }
    }
    
    @Action([Encounters.NoteAddendumAdded])
    async noteAddendumAdded
    (
        ctx: StateContext<EncounterStateModel>,
        action: Encounters.NoteAddendumAdded
    )
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            notes: updateItem(
                                n => n.id == action.payload.noteId,
                                patch({ addenda: append([plainToInstance(NoteAddendum, action.payload.addendum)]) })
                            )
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }
    }

    @Action([Encounters.DiaryAdded, Encounters.DiaryUpdated])
    async encounterDiaryAdded
    (
        ctx:StateContext<EncounterStateModel>,
        action:Encounters.DiaryAdded | Encounters.DiaryUpdated
    )
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            diaries: iif( action instanceof Encounters.DiaryAdded ,append([plainToInstance(Diary,action.payload.diary)]), updateItem(n => n.id == action.payload.diary.id, plainToInstance(Diary,action.payload.diary)))
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }

        if(action instanceof Encounters.DiaryAdded)
            await lastValueFrom(ctx.dispatch(new Referrals.DiaryAdded({encounterId:action.payload.encounterId,diary:action.payload.diary})));
        else
            await lastValueFrom(ctx.dispatch(new Referrals.DiaryUpdated({encounterId:action.payload.encounterId,diary:action.payload.diary})));

    }




    @Action([Encounters.MedicalBillSet, 
            Encounters.MedicalBillRemoved, Encounters.MedicalBillUpdated,
            Encounters.MedicalBillImageAdded, Encounters.MedicalBillImageRemoved,
            Encounters.AdditionalMedicalBillRemoved, Encounters.MedicalBillAdded,
            Encounters.FinanceServiceEncounterInvoiceCreated, Encounters.FinanceServiceEncounterBillCreated],{cancelUncompleted: true})
    encounterRelatedDetailsChanged
    (
        ctx:StateContext<EncounterStateModel>, 
        action: Encounters.MedicalBillSet |  Encounters.MedicalBillImageAdded |
                Encounters.MedicalBillRemoved | Encounters.MedicalBillImageRemoved |
                Encounters.MedicalBillUpdated | Encounters.FinanceServiceEncounterInvoiceCreated |
                Encounters.FinanceServiceEncounterBillCreated)
    {
        return ctx.dispatch(new BlockUI.BlockUI())
        .pipe
        (
            switchMap
            (
                val => 
                forkJoin
                (
                    [
                        this._encounterService.getEncounter(action.payload.encounterId),
                        this._encounterService.getEncounterHeader(action.payload.encounterId)
                    ]
                ),
            ),
            tap
            (
                val => 
                {
                    if(val[0])
                        ctx.dispatch(new Encounters.EncounterUpdated({header:val[1],encounter:val[0]}));
            
                }
            )
        )

                
    }

    @Action([Encounters.EncounterLocked, Encounters.EncounterUnlocked],{cancelUncompleted: true})
    encounterDetailsChanged
    (
        ctx: StateContext<EncounterStateModel>,
        action: Encounters.EncounterLocked | Encounters.EncounterUnlocked
    )
    {
        var encounter = action.payload.encounter;
        
        return ctx.dispatch(new BlockUI.BlockUI())
        .pipe
        (
            switchMap
            (
                val=> this._encounterService.getEncounterHeader(action.payload.encounter.id),
            ),
            tap
            (
                header => 
                {
                    if(header)
                        ctx.dispatch(new Encounters.EncounterUpdated({header:header,encounter:encounter}));
            
                }
            )
        )
    }
    

    @Action([Encounters.EncounterServiceEncounterFinancialsPosted, Encounters.EncounterServiceEncounterFinancialsUnPosted],{cancelUncompleted: true})
    encounterFinancialsPosted
    (
        ctx: StateContext<EncounterStateModel>,
        action: Encounters.EncounterServiceEncounterFinancialsPosted | Encounters.EncounterServiceEncounterFinancialsUnPosted
    )
    {
        var financials = action.payload.financials;
        
        return ctx.dispatch(new BlockUI.BlockUI())
        .pipe
        (
            switchMap
            (
                val=> this._encounterService.getEncounter(action.payload.financials.encounter.id),
            ),
            tap
            (
                enc => 
                {
                    if(enc)
                        ctx.dispatch(new Encounters.EncounterUpdated({encounter:enc,header:financials.encounter}));
            
                }
            )
        )
    }


    @Action([Encounters.AppointmentAdded,Encounters.AppointmentCancelled])
    appointmentAdded(ctx:StateContext<EncounterStateModel>, action: Encounters.AppointmentAdded | Encounters.AppointmentCancelled)
    {
        return ctx.dispatch(new BlockUI.BlockUI())
        .pipe
        (
            switchMap
            (
                val => 
        
                forkJoin
                (
                    [this._encounterService.getEncounter(action.payload.encounterId),
                    this._encounterService.getEncounterHeader(action.payload.encounterId)]
                )
            ),
            tap
            (
                val => 
                {
                    var encounter = val[0];
                    var header = val[1]
                    // if(encounter)
                    //     ctx.dispatch(new Encounters.EncounterUpdated({header:header,encounter:encounter}));
                    
                    if(! encounter.appointments)
                        encounter.appointments = [];
                    
                    encounter.appointments = [action.payload.appointment, ...encounter.appointments.filter(a => a.id != action.payload.appointment.id)];
                    
                    if(encounter)
                        ctx.dispatch(new Encounters.EncounterUpdated({header:header,encounter:encounter}));
                 }
            )
        )
    }

    // @Action([Encounters.FinanceServiceEncounterInvoiceCreated])
    // invoiceCreated(ctx:StateContext<EncounterStateModel>, action:Encounters.FinanceServiceEncounterInvoiceCreated)
    // {
    //     //Find which (if any encounters) are affected
        
    // }

  }


interface PersistedEncounters
{
    persistedEncounterIds: number[],
    activeEncounterIndex: number,
    activeEncounterFilter?: EncounterFilter

}